import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../lib/api';
import { enablePush, getCurrentSubscription, getPermission, isIOS, wasTurnedOff } from '../../lib/push';

const DISMISS_KEY = 'mp_push_banner_dismissed_at';
const DISMISS_DAYS = 14;

function dismissedRecently() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch (_) { return false; }
}

// Inicio banner for a phone without working push (state from push.js
// syncPushOnLoad, run on every app load): iOS drops subscriptions of
// installed apps and a dead one gets pruned server-side, so people lose
// notifications without having turned them off. Hidden when this device
// switched them off in Settings, or for 14 days after "x".
export default function PushBanner({ pushState, onEnabled }) {
  const [hidden, setHidden] = useState(dismissedRecently);
  const [busy, setBusy] = useState(false);
  const [liveOk, setLiveOk] = useState(false);

  // pushState is from app load; push may have been turned on since (Settings).
  useEffect(() => {
    if (!pushState?.supported || pushState.subscribed) return;
    getCurrentSubscription()
      .then(sub => setLiveOk(!!sub && getPermission() === 'granted'))
      .catch(() => {});
  }, [pushState]);

  if (liveOk || !pushState?.supported || pushState.subscribed || hidden || wasTurnedOff()) return null;

  const blocked = pushState.permission === 'denied';

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* storage unavailable */ }
    setHidden(true);
  };

  const activate = async () => {
    setBusy(true);
    try {
      await enablePush();
      const updated = await api.updateProfile({ notifications_enabled: true, tz_offset: new Date().getTimezoneOffset() });
      onEnabled?.(updated);
      setHidden(true);
      toast.success('Notificaciones activadas');
      api.sendTestPush().catch(() => {});
    } catch (e) {
      toast.error(e?.code === 'permission-denied'
        ? 'El permiso quedó bloqueado. Actívalo en los ajustes del teléfono.'
        : 'No se pudieron activar las notificaciones');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-4 flex items-start gap-3" data-testid="push-banner">
      <div className="w-9 h-9 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
        <BellRing className="w-5 h-5 text-violet-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">Tus notificaciones están apagadas</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {blocked
            ? (isIOS()
              ? 'Actívalas en Ajustes → Notificaciones → MiPisto.'
              : 'Actívalas en los ajustes del sitio de tu navegador.')
            : 'Actívalas para recibir tus alertas de presupuesto y los gastos detectados.'}
        </p>
        {!blocked && (
          <button
            onClick={activate}
            disabled={busy}
            className="mt-2.5 bg-brand hover:bg-brand-hover text-on-brand px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            {busy ? 'Activando…' : 'Activar notificaciones'}
          </button>
        )}
      </div>
      <button onClick={dismiss} aria-label="Cerrar" className="hit-44 text-slate-500 hover:text-slate-300 shrink-0">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
